import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface Notification {
  id?: string;
  type: "info" | "success" | "error" | "warning" | "loading";
  message: string;
  title?: string;
  duration: number;
  dismissible?: boolean;
  pauseOnHover?: boolean;
  timeoutId?: NodeJS.Timeout | null;
  createdAt?: number;
  remainingTime?: number;
  pausedAt?: number;
}

export interface NotificationOptions {
  title?: string;
  duration?: number;
  dismissible?: boolean;
  pauseOnHover?: boolean;
}

interface NotificationsStore {
  notifications: Notification[];
  maxNotifications: number;
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
  error: (message: string, options?: NotificationOptions) => void;
  info: (message: string, options?: NotificationOptions) => void;
  success: (message: string, options?: NotificationOptions) => void;
  warning: (message: string, options?: NotificationOptions) => void;
  loading: (message: string, options?: NotificationOptions) => string;
  promise: (
    promise: Promise<any>,
    messages: { loading?: string; success?: string; error?: string },
    options?: NotificationOptions
  ) => Promise<any>;
  pauseNotification: (id: string) => void;
  resumeNotification: (id: string) => void;
}

export const useNotificationStore = create<NotificationsStore>()(
  devtools(
    (set, get) => ({
      notifications: [],
      maxNotifications: 5,

      addNotification: (notification) => {
        const state = get();

        const isDuplicate = state.notifications.some(
          (n) =>
            n.message === notification.message && n.type === notification.type
        );
        if (isDuplicate) return;

        let notifications = [...state.notifications];

        const id =
          Date.now().toString(36) + Math.random().toString(36).substr(2);

        if (notifications.length >= state.maxNotifications) {
          notifications = notifications.slice(-(state.maxNotifications - 1));
        }

        const newNotification = {
          id,
          type: notification.type || "info",
          message: notification.message,
          title: notification.title,
          duration: notification.duration ?? 4000,
          dismissible: notification.dismissible ?? true,
          pauseOnHover: notification.pauseOnHover ?? false,
          createdAt: Date.now(),
          timeoutId: null,
          remainingTime: notification.duration ?? 4000,
        };

        notifications.push(newNotification);
        set({ notifications });

        if (newNotification.duration > 0) {
          const timeoutId = setTimeout(() => {
            get().removeNotification(id);
          }, newNotification.duration);

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, timeoutId } : n
            ),
          }));
        }

        return id;
      },

      removeNotification: (id) => {
        const state = get();
        const notification = state.notifications.find((n) => n.id === id);

        if (notification?.timeoutId) {
          clearTimeout(notification.timeoutId);
        }

        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      },

      pauseNotification: (id: string) => {
        const state = get();
        const notification = state.notifications.find((n) => n.id === id);

        if (notification?.timeoutId) {
          clearTimeout(notification.timeoutId);

          const elapsed = Date.now() - (notification.createdAt ?? Date.now());
          const remainingTime = Math.max(0, notification.duration - elapsed);

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id
                ? { ...n, timeoutId: null, remainingTime, pausedAt: Date.now() }
                : n
            ),
          }));
        }
      },

      resumeNotification: (id: string): void => {
        const state = get();
        const notification = state.notifications.find((n) => n.id === id);

        if (notification && (notification.remainingTime ?? 0) > 0) {
          const timeoutId: NodeJS.Timeout = setTimeout(() => {
            get().removeNotification(id);
          }, notification.remainingTime ?? 0);

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.id === id
                ? {
                    ...n,
                    timeoutId,
                    remainingTime: n.remainingTime ?? 0,
                    pausedAt: undefined,
                  }
                : n
            ),
          }));
        }
      },

      success: (message, options = {}) => {
        return get().addNotification({
          message,
          type: "success",
          duration: 4000,
          ...options,
        });
      },

      loading: (message, options = {}) => {
        return get().addNotification({
          message,
          type: "loading",
          duration: 0,
          ...options,
        });
      },

      error: (message, options = {}) => {
        return get().addNotification({
          message,
          type: "error",
          duration: 6000,
          ...options,
        });
      },

      warning: (message, options = {}) => {
        return get().addNotification({
          message,
          type: "warning",
          duration: 4000,
          ...options,
        });
      },

      info: (message, options = {}) => {
        return get().addNotification({
          message,
          type: "info",
          duration: 4000,
          ...options,
        });
      },

      promise: async (promise, messages, options = {}) => {
        const loadingId = get().loading(
          messages.loading || "Loading...",
          options
        );

        try {
          const result = await promise;
          get().removeNotification(loadingId);
          get().success(messages.success || "Success!", options);
          return result;
        } catch (error) {
          get().removeNotification(loadingId);
          get().error(messages.error || "Something went wrong", options);
          throw error;
        }
      },
    }),
    {
      name: "NotificationsStore",
    }
  )
);
